import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { ToolSummary } from '../../api/TraceabilityResponseType';
import {
  filterReferenceOptions,
  findNearReference,
  findReference,
  referenceOptions,
  type ReferenceOption,
} from '../../utils/toolSummaries';
import { TextCombobox } from '@/components/form/inputs/TextCombobox';
import { Highlight } from '@/components/form/inputs/combobox/ComboboxPopup';
import { PickerStatus } from './PickerStatus';
import { countText } from './pickerHelpers';

type Row = { kind: 'reference'; option: ReferenceOption } | { kind: 'create'; value: string };

export type ReferencePickerProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The company's tools; null (loading, unavailable, other company) → plain text field. */
  summaries: readonly ToolSummary[] | null;
  /** Reference the tool has now (reference edit): marked in the list, no "new" warning for it. */
  currentReference?: string | null;
  /** Example shown by the plain text fallback (the list has its own search hint). */
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

/**
 * Reference field of the tool forms: a list of the company's references with their tool count,
 * and a status line that says plainly when the value would create a new reference.
 */
export function ReferencePicker({
  id,
  value,
  onChange,
  summaries,
  currentReference,
  placeholder,
  required,
  disabled,
}: ReferencePickerProps) {
  const { t } = useTranslation();
  const { aliasFor } = useReferenceAlias();
  const options = useMemo(() => (summaries ? referenceOptions(summaries) : []), [summaries]);
  const statusId = `${id}-status`;
  const trimmed = value.trim();

  if (!summaries) {
    const alias = aliasFor(trimmed);
    return (
      <>
        <Input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            {t('referenceAliases.helperText', { alias })}
          </p>
        )}
      </>
    );
  }

  const exact = findReference(trimmed, options);
  const rows: Row[] = filterReferenceOptions(value, options, aliasFor).map((option) => ({
    kind: 'reference',
    option,
  }));
  if (trimmed && !exact) rows.push({ kind: 'create', value: trimmed });

  return (
    <>
      <TextCombobox<Row>
        id={id}
        value={value}
        onChange={onChange}
        options={rows}
        optionValue={(row) => (row.kind === 'reference' ? row.option.reference : row.value)}
        optionKey={(row) => (row.kind === 'reference' ? `ref:${row.option.reference}` : 'create')}
        optionClassName={(row) =>
          row.kind === 'create' ? 'border-t border-border font-medium text-warning' : undefined
        }
        renderOption={(row) =>
          row.kind === 'create' ? (
            <>
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">
                {t('toolPickers.reference.create', { reference: row.value })}
              </span>
            </>
          ) : (
            <ReferenceRow
              option={row.option}
              alias={aliasFor(row.option.reference)}
              query={value}
              isCurrent={row.option.reference === currentReference}
            />
          )
        }
        header={t('toolPickers.reference.listTitle', { count: options.length })}
        emptyLabel={t('toolPickers.reference.empty')}
        placeholder={t('toolPickers.reference.placeholder')}
        required={required}
        disabled={disabled}
        describedBy={statusId}
      />
      <ReferenceStatus
        id={statusId}
        value={trimmed}
        options={options}
        currentReference={currentReference}
        onUse={onChange}
      />
    </>
  );
}

function ReferenceRow({
  option,
  alias,
  query,
  isCurrent,
}: {
  option: ReferenceOption;
  alias: string | undefined;
  query: string;
  isCurrent: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className="flex min-w-0 flex-1 flex-col">
        {alias && (
          <span className="truncate font-semibold">
            <Highlight text={alias} query={query} />
          </span>
        )}
        <span
          className={
            alias ? 'truncate font-mono text-xs text-muted-foreground' : 'truncate font-mono'
          }
        >
          <Highlight text={option.reference} query={query} />
        </span>
      </span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {countText(t, 'toolPickers.reference.toolCount', option.toolCount)}
        {isCurrent && ` · ${t('toolPickers.reference.current')}`}
      </span>
    </>
  );
}

function ReferenceStatus({
  id,
  value,
  options,
  currentReference,
  onUse,
}: {
  id: string;
  value: string;
  options: readonly ReferenceOption[];
  currentReference?: string | null;
  onUse: (reference: string) => void;
}) {
  const { t } = useTranslation();
  const { aliasFor } = useReferenceAlias();
  const testId = 'reference-status';

  if (!value) {
    return (
      <PickerStatus kind="hint" id={id} testId={testId}>
        {t('toolPickers.reference.hint')}
      </PickerStatus>
    );
  }
  if (currentReference && value === currentReference) {
    return (
      <PickerStatus kind="hint" id={id} testId={testId}>
        {t('toolPickers.reference.currentHint')}
      </PickerStatus>
    );
  }
  const exact = findReference(value, options);
  if (exact) {
    const alias = aliasFor(exact.reference);
    const tools = countText(t, 'toolPickers.reference.toolCount', exact.toolCount);
    return (
      <PickerStatus kind="ok" id={id} testId={testId}>
        {alias
          ? t('toolPickers.reference.existingAlias', { tools, alias })
          : t('toolPickers.reference.existing', { tools })}
      </PickerStatus>
    );
  }
  const near = findNearReference(value, options);
  if (near) {
    return (
      <PickerStatus kind="near" id={id} testId={testId}>
        <strong>{t('toolPickers.reference.nearTitle')}</strong>{' '}
        {t('toolPickers.reference.near', {
          reference: near.reference,
          tools: countText(t, 'toolPickers.reference.toolCount', near.toolCount),
          value,
        })}{' '}
        <Button type="button" variant="link" size="inline" onClick={() => onUse(near.reference)}>
          {t('toolPickers.reference.use', { reference: near.reference })}
        </Button>
      </PickerStatus>
    );
  }
  return (
    <PickerStatus kind="new" id={id} testId={testId}>
      <strong>{t('toolPickers.reference.newTitle')}</strong>{' '}
      {t('toolPickers.reference.new', { value })}
    </PickerStatus>
  );
}
